import React, { useEffect } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import {
  Card,
  Link,
  Stack,
  SkeletonPage,
  SkeletonBodyText
} from '@shopify/polaris';

import { useUser } from '../Providers/UserProvider';
import { fetchReview, generateReview } from '../../utils/woorankApi';
import { getRelativeDate } from '@hydra/utils/formatDate';

import { Skeleton } from '@material-ui/lab';
import { useI18n } from '@hydra/common/customHooks';
import { ScreenshotThumbnail } from '@hydra/routes/dashboard/components/DesktopBanner';
import ScoreCircle from '@hydra/common/components/ScoreCircle';

import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  centered: { textAlign: 'center' },
  hiddenXs: { [theme.breakpoints.down('xs')]: { display: 'none' } },
  hiddenSm: { [theme.breakpoints.down('sm')]: { display: 'none' } },
  visibleXs: {
    display: 'none',
    [theme.breakpoints.down('xs')]: { display: 'block' }
  },
  dashboardContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .leftSide': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(1.25, 1),
      overflow: 'hidden'
    },
    '& .rightSide': {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center'
    },
    '& .urlText': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      overflow: 'hidden'
    }
  }
}));

export function ReviewDashboardDisplayLoadingState () {
  const classes = useStyles();
  return (
    <>
      <div className={classes.hiddenXs}>
        <Stack alignment='center'>
          <Stack.Item>
            <ScreenshotThumbnail isLoading screenshot='' />
          </Stack.Item>
          <Stack.Item fill>
            <SkeletonBodyText lines={3} />
          </Stack.Item>
          <Stack.Item>
            <Skeleton variant='circle' height={100} width={100} />
          </Stack.Item>
        </Stack>
      </div>
      <div className={classes.visibleXs}>
        <Stack alignment='center'>
          <Stack.Item fill>
            <SkeletonBodyText lines={2} />
            <ScreenshotThumbnail isLoading screenshot='' />
          </Stack.Item>
        </Stack>
      </div>
    </>
  );
}

function ReviewDashboardDisplay ({ reviewData, isLoading } : { reviewData: any, isLoading: boolean }) {
  const classes = useStyles();
  const { moment, translate } = useI18n();
  const { score, url, screenshot, lastGeneratedAt } = reviewData;

  const scoreHref = `/shopify/review-display/${encodeURIComponent(url)}`;
  const relative = (!lastGeneratedAt || new Date(lastGeneratedAt).getTime() === 0)
    ? ''
    : getRelativeDate({ date: lastGeneratedAt, moment });

  return (
    isLoading
      ? <ReviewDashboardDisplayLoadingState />
      : (
        <>
          <div className={classes.hiddenXs}>
            <div className={classes.dashboardContainer}>
              <div className='leftSide'>
                <ScreenshotThumbnail isLoading={isLoading} screenshot={screenshot} />
                <div className='urlText'>
                  <Typography noWrap component='h2' variant='h3'>
                    {url}
                  </Typography>
                  {relative}
                </div>
              </div>
              <div className='rightSide'>
                <Typography className={classes.hiddenSm}>
                  {`${translate('homepage_score')}:`}
                </Typography>
                <Link url={scoreHref}>
                  <ScoreCircle
                    showProgress
                    score={score}
                    size='medium'
                    animate={false}
                    fireworks
                  />
                </Link>
              </div>
            </div>
          </div>
          <div className={classes.visibleXs}>
            <Stack distribution='center' vertical spacing='tight' alignment='center'>
              <Stack spacing='extraLoose' alignment='center' vertical>
                <div className={classes.centered}>
                  <Typography noWrap component='h2' variant='h3'>
                    {url}
                  </Typography>
                  {relative}
                </div>
              </Stack>
              <Stack spacing='extraTight' alignment='center'>
                <ScreenshotThumbnail isLoading={isLoading} screenshot={screenshot} />
                <Link url={scoreHref}>
                  <ScoreCircle
                    showProgress
                    score={score}
                    size='medium'
                    animate={false}
                    fireworks
                  />
                </Link>
              </Stack>
            </Stack>
          </div>
        </>
        )
  );
}

interface ReviewGenerationProps {
  url: string;
  apiKey: string;
}
function ReviewGeneration ({ url, apiKey }: ReviewGenerationProps) {
  const { mutate } = useSWRConfig();

  useEffect(() => {
    generateReview(url, apiKey); // todo, use the cleaned up url here
    const pollingInterval = setInterval(async () => {
      const reviewData = await fetchReview(url, apiKey);
      if (reviewData) {
        mutate(`/woorank-api/review/${url}`);
        clearInterval(pollingInterval);
      }
    }, 15000);
    return () => clearInterval(pollingInterval);
  }, [url, apiKey, mutate]);
  return (<SkeletonPage />);
}

interface BannerProps {
  url: string
}
export default function Banner ({ url }: BannerProps) {
  const { loading: userLoading, user } = useUser();
  const { data: reviewData, error } = useSWR(
    user?.apiKey ? `/woorank-api/review/${url}` : null,
    () => fetchReview(url, user!.apiKey)
  );
  const loading = userLoading || reviewData === undefined;

  return (
    <Card sectioned>
      {
        error
          ? (<><h1>Error</h1></>) // Todo: <ErrorPage error={error} />
          : loading
            ? <ReviewDashboardDisplay reviewData={{}} isLoading />
            : reviewData === null
              ? <ReviewGeneration url={url} apiKey={user!.apiKey || ''} />
              : <ReviewDashboardDisplay reviewData={reviewData} isLoading={loading} />
      }
    </Card>
  );
}
